import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";

// These values are not secrets — a Firebase web config identifies the project
// to the client and is visible in any deployed bundle. Access is controlled by
// Security Rules and by the server, never by hiding this.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

/**
 * Serve the app from the same origin its sign-in comes back to, or send the
 * browser there.
 *
 * Sign-in leaves for Google and returns to `authDomain`. When the page is open
 * on a *different* host, the credential lands in storage the page cannot read —
 * browsers partition it — and the sign-in screen simply reappears with no error
 * anywhere. Production was fixed by pointing `authDomain` at
 * `crm.chbapark.org`, the host the app is actually served from.
 *
 * Dev cannot be fixed that way without a console change: its OAuth client still
 * rejects `chbapark-crm-dev.web.app/__/auth/handler` with
 * `redirect_uri_mismatch`, verified against Google. So `.web.app` is a URL where
 * nobody can sign in — and it is the URL in everybody's history, which is how
 * the product owner kept landing on it weeks after production was working.
 *
 * Rather than ask people to remember which of two identical-looking addresses
 * works, the app moves itself. Both hostnames serve the same site, so this is a
 * hop, not a redirect chain — and it cannot loop, because it only fires when the
 * host differs from `authDomain` and `authDomain` is always a host Firebase
 * serves.
 *
 * Only Firebase's own hosting domains are rewritten. A custom domain that is not
 * the `authDomain` is a deliberate configuration, not an accident, and is left
 * alone.
 */
const authDomain = firebaseConfig.authDomain as string | undefined;
if (
  typeof window !== "undefined" &&
  authDomain &&
  window.location.hostname !== authDomain &&
  /\.(web\.app|firebaseapp\.com)$/.test(window.location.hostname)
) {
  const { pathname, search, hash } = window.location;
  window.location.replace(`https://${authDomain}${pathname}${search}${hash}`);
}

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app, "me-west1");

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

export const WORKSPACE_ID = "chbapark";
